/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useState } from "react"
import { observer } from "mobx-react-lite"
import { LoadingButton } from "@mui/lab"
import { Divider } from "@mui/material"
import Loading from "@medaica/common/components/loading"
import { logError } from "@medaica/common/services/util"
import { PatientReportFacts } from "@medaica/common/types"
import { useHeartReportStores } from "../heart-report-context"
import HeartReportLayout, { HeartReportCard } from "../components/heart-report-layout"
import heartReportCopy from "../heartReportCopy"

const copy = heartReportCopy.download

const DOCTOR_COPY_HEADING_ID = "heart-report-doctor-copy-heading"

// e.g. "Sep 1, 2026" — the release date in the viewer's local time zone.
const formatReleasedAt = (releasedAt: Date): string =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(releasedAt)

// "Exam date: … · Released: …", skipping a fact the report doesn't carry. The reviewing
// organization is not shown: organization names are internal to Medaica and its providers.
const factsLine = (facts: PatientReportFacts): string =>
  [facts.examDate ? copy.examDate(facts.examDate) : null, copy.released(formatReleasedAt(facts.releasedAt))]
    .filter((part): part is string => part !== null)
    .join(copy.factsSeparator)

// One download button's state. Each button gets its own in-flight flag and error, so a
// spinner or a failure on one never shows on the other.
const useDownload = (
  run: () => Promise<void>,
  failedMessage: string
): { downloading: boolean; error: string | null; start: () => Promise<void> } => {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = async (): Promise<void> => {
    setDownloading(true)
    setError(null)
    try {
      await run()
      // an ended session or a withdrawn report is routed by the store
    } catch (downloadError) {
      logError(downloadError)
      setError(failedMessage)
    } finally {
      setDownloading(false)
    }
  }

  return { downloading, error, start }
}

const DownloadError = ({ message }: { message: string | null }): ReactElement | null =>
  message ? (
    <p role="alert" className="mb-3 text-center text-sm text-red-600">
      {message}
    </p>
  ) : null

/**
 * The download screen: the report's header facts and the download button. Nothing from
 * the report body is rendered here — the PDF is fetched with the session token and handed
 * to the browser as a file; the button may be used more than once.
 *
 * When the report comes with a clinician-format copy, a second, visually quieter block
 * below a divider offers it for the patient to pass on to their own doctor. The patient's
 * own report stays the one filled button; without that copy the second block and the
 * helper line under the main button are absent altogether.
 */
const Download = observer((): ReactElement => {
  const { sessionStore } = useHeartReportStores()
  const ownReport = useDownload(() => sessionStore.downloadPdf(), copy.errors.downloadFailed)
  const doctorCopy = useDownload(() => sessionStore.downloadProviderPdf(), copy.errors.doctorCopyDownloadFailed)
  const { facts } = sessionStore

  if (!facts) {
    return (
      <HeartReportLayout>
        <div className="py-24">
          <Loading />
        </div>
      </HeartReportLayout>
    )
  }

  const hasDoctorCopy = Boolean(facts.providerReport)

  return (
    <HeartReportCard title={copy.title(facts.patientName)}>
      <p className="mt-2 mb-8 text-center text-sm text-gray-600">{factsLine(facts)}</p>

      <DownloadError message={ownReport.error} />

      <LoadingButton
        fullWidth
        variant="contained"
        size="large"
        loading={ownReport.downloading}
        onClick={() => void ownReport.start()}
      >
        {copy.downloadButton}
      </LoadingButton>
      {hasDoctorCopy ? <p className="mt-2 text-center text-sm text-gray-600">{copy.ownReportNote}</p> : null}

      {hasDoctorCopy ? (
        <section aria-labelledby={DOCTOR_COPY_HEADING_ID}>
          <Divider sx={{ mt: 4, mb: 3 }} />
          <h2 id={DOCTOR_COPY_HEADING_ID} className="text-base font-bold text-center text-gray-900">
            {copy.doctorCopy.heading}
          </h2>
          <p className="mt-1 mb-4 text-center text-sm text-gray-600">{copy.doctorCopy.body}</p>

          <DownloadError message={doctorCopy.error} />

          <LoadingButton
            fullWidth
            variant="outlined"
            loading={doctorCopy.downloading}
            onClick={() => void doctorCopy.start()}
          >
            {copy.doctorCopy.downloadButton}
          </LoadingButton>
          {/* Closes the block, so the disclaimer below reads as applying to the whole page */}
          <Divider sx={{ mt: 4 }} />
        </section>
      ) : null}

      <p className="mt-6 text-sm text-gray-600">{copy.disclaimer}</p>
      <p className="mt-4 text-center text-sm text-gray-500">{copy.sessionNote}</p>
    </HeartReportCard>
  )
})

export default Download
